import { useRef, useState } from 'react';
import {
  PROJECT_DONE_COLOR,
  PROJECT_DONE_LABEL,
  PROJECT_PRIORITY_COLORS,
  PROJECT_PRIORITY_LABELS,
  PROJECT_PRIORITY_ORDER,
} from '../../pm/constants';
import { ProjectPriority } from '../../pm/types';
import { IconChevDown } from './Icons';
import PortalMenu from './PortalMenu';

// What the picker can emit: a real priority, `null` (Unprioritized), or the
// 'done' sentinel (completed — moves the project to the Done rail). Done is
// not a ProjectPriority because done projects leave the priority lanes
// entirely, so it travels as its own discriminant.
export type PriorityChoice = ProjectPriority | null | 'done';

interface Props {
  // `null`/undefined = "Unprioritized" — surfaced as a clearable state.
  priority?: ProjectPriority;
  // When true the project is completed; the trigger reads "Done" and the
  // Done option is checked regardless of the underlying priority.
  done?: boolean;
  onChange: (next: PriorityChoice) => void;
}

const ProjectPriorityPicker: React.FC<Props> = ({ priority, done, onChange }) => {
  const [open, setOpen] = useState(false);
  const triggerRef = useRef<HTMLSpanElement | null>(null);

  const label = done
    ? PROJECT_DONE_LABEL
    : priority
    ? PROJECT_PRIORITY_LABELS[priority]
    : 'Unprioritized';
  const color = done ? PROJECT_DONE_COLOR : priority ? PROJECT_PRIORITY_COLORS[priority] : undefined;

  function pick(next: PriorityChoice) {
    onChange(next);
    setOpen(false);
  }

  return (
    <span className="pm-meta-pick-wrap" ref={triggerRef}>
      <button
        type="button"
        className="pm-meta-pick"
        onClick={() => setOpen((v) => !v)}
        title="Click to change priority"
      >
        {color ? (
          <span className="pm-priority-dot" style={{ background: color }} />
        ) : (
          <span className="pm-priority-dot pm-priority-dot-unset" />
        )}
        <span className="pm-meta-pick-label">{label}</span>
        <IconChevDown size={12} />
      </button>
      <PortalMenu
        open={open}
        triggerRef={triggerRef}
        onDismiss={() => setOpen(false)}
        className="pm-priority-menu"
        estimatedHeight={(PROJECT_PRIORITY_ORDER.length + 2) * 36 + 24}
      >
        {PROJECT_PRIORITY_ORDER.map((p) => (
          <button
            key={p}
            className={!done && p === priority ? 'active' : ''}
            onClick={() => pick(p)}
          >
            <span
              className="pm-priority-dot"
              style={{ background: PROJECT_PRIORITY_COLORS[p] }}
            />
            {PROJECT_PRIORITY_LABELS[p]}
          </button>
        ))}
        <button
          className={!done && !priority ? 'active' : ''}
          onClick={() => pick(null)}
        >
          Unprioritized
        </button>
        <div className="pm-priority-menu-sep" />
        <button
          className={done ? 'active' : ''}
          onClick={() => pick('done')}
        >
          <span className="pm-priority-dot" style={{ background: PROJECT_DONE_COLOR }} />
          {PROJECT_DONE_LABEL}
        </button>
      </PortalMenu>
    </span>
  );
};

export default ProjectPriorityPicker;
